import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const nodes = [
  { label: 'WOTECH', icon: 'ri-cpu-line', color: '#0891B2', bg: '#ECFEFF', size: 'lg' },
  { label: 'ManageEngine', icon: 'ri-settings-3-line', color: '#0891B2', bg: '#ECFEFF', size: 'md' },
  { label: 'AutomMate', icon: 'ri-robot-2-line', color: '#F97316', bg: '#FFF7ED', size: 'md' },
  { label: 'Zoho ONE', icon: 'ri-apps-line', color: '#0891B2', bg: '#ECFEFF', size: 'md' },
  { label: 'ITSM', icon: 'ri-customer-service-2-line', color: '#64748B', bg: '#F1F5F9', size: 'sm' },
  { label: 'UEM', icon: 'ri-computer-line', color: '#64748B', bg: '#F1F5F9', size: 'sm' },
  { label: 'AD Yönetimi', icon: 'ri-shield-user-line', color: '#64748B', bg: '#F1F5F9', size: 'sm' },
  { label: 'Monitoring', icon: 'ri-line-chart-line', color: '#64748B', bg: '#F1F5F9', size: 'sm' },
  { label: 'Otomasyon', icon: 'ri-flow-chart', color: '#64748B', bg: '#F1F5F9', size: 'sm' },
  { label: 'Entegrasyon', icon: 'ri-link', color: '#64748B', bg: '#F1F5F9', size: 'sm' },
];

const TechUniverse = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.tu-header', { scrollTrigger: { trigger: '.tu-header', start: 'top 85%' }, opacity: 0, y: 30, duration: 0.9, ease: 'power3.out' });
      gsap.from('.tu-node', {
        scrollTrigger: { trigger: '.tu-grid', start: 'top 70%' },
        opacity: 0, scale: 0.8, duration: 0.6, stagger: 0.07, ease: 'back.out(1.5)',
      });
      gsap.from('.tu-img', { scrollTrigger: { trigger: '.tu-img', start: 'top 75%' }, opacity: 0, y: 30, duration: 0.9, ease: 'power3.out' });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const sizeMap: Record<string, string> = {
    lg: 'w-28 h-28',
    md: 'w-24 h-24',
    sm: 'w-20 h-20',
  };
  const iconSizeMap: Record<string, string> = { lg: 'text-3xl', md: 'text-2xl', sm: 'text-xl' };
  const textSizeMap: Record<string, string> = { lg: 'text-xs font-bold', md: 'text-xs font-semibold', sm: 'text-xs' };

  return (
    <section ref={sectionRef} className="py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="tu-header text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#ECFEFF] border border-[#0891B2]/30 text-[#0E7490] text-xs font-semibold mb-5 font-body">
            <i className="ri-node-tree"></i>
            Ekosistem
          </div>
          <h2 className="text-4xl font-extrabold text-[#0F172A] mb-4 font-heading">
            WOTECH Tech Universe
          </h2>
          <p className="text-base text-[#475569] font-body">
            Tüm IT ekosisteminiz, tek bir çatı altında birleşiyor.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Node diagram */}
          <div className="tu-grid flex flex-wrap justify-center gap-4">
            {nodes.map((n, i) => (
              <div
                key={i}
                className={`tu-node ${sizeMap[n.size]} flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-[#E2E8F0] cursor-default transition-all duration-200 hover:scale-105`}
                style={{ background: n.bg, border: `1.5px solid ${n.color}25` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${n.color}15` }}>
                  <i className={`${n.icon} ${iconSizeMap[n.size]}`} style={{ color: n.color }}></i>
                </div>
                <span className={`${textSizeMap[n.size]} text-center leading-tight px-1 text-[#334155] font-body`}>{n.label}</span>
              </div>
            ))}
          </div>

          {/* Descriptive image */}
          <div className="tu-img">
            <div className="rounded-2xl overflow-hidden border border-[#E2E8F0]">
              <img
                src="https://public.readdy.ai/ai/img_res/56c1610095eae823257f6919051543be.jpg"
                alt="WOTECH IT Ekosistemi"
                title="Tüm IT çözümleri tek çatı altında"
                className="w-full h-80 object-cover object-top"
              />
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3">
              {[
                { label: 'ManageEngine', sub: 'ITSM & UEM & Monitoring', icon: 'ri-settings-3-line', color: '#0891B2' },
                { label: 'AutomMate', sub: 'No-code Otomasyon', icon: 'ri-robot-2-line', color: '#F97316' },
                { label: 'Zoho ONE', sub: 'İş Uygulamaları', icon: 'ri-apps-line', color: '#0891B2' },
              ].map((p, i) => (
                <div key={i} className="p-4 rounded-xl bg-white border border-[#E2E8F0] text-center">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-2" style={{ background: `${p.color}15` }}>
                    <i className={`${p.icon} text-base`} style={{ color: p.color }}></i>
                  </div>
                  <div className="text-xs font-bold text-[#0F172A] font-heading">{p.label}</div>
                  <div className="text-xs text-[#64748B] font-body mt-0.5 leading-tight">{p.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechUniverse;
